import { useEffect, useRef, useCallback } from 'react';
import { request, requestWithParam } from '../utility/apiClients';
import { APIS_ENDPOINTS, APIS_METHOD } from '../resources/api-constants';
import { isTokenAvailable } from '../utility/helpers';
import { shouldTrackAnalytics } from '../analytics/track';

interface AnalyticsConfig {
    sessionId: string;
    userId?: number;
    pageUrl: string;
    pageTitle: string;
    /** When true, tracking runs; when consent is granted on same page, effect re-runs */
    hasConsent: boolean;
}

interface TrackEventParams {
    eventType: string;
    eventName: string;
    elementId?: string;
    elementClass?: string;
    metadata?: Record<string, any>;
}

// Helper functions
function getDeviceType(): string {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
}

function getBrowser(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Edg')) return 'Edge';
    return 'Unknown';
}

function getOS(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    return 'Unknown';
}

export const useAnalytics = (config: AnalyticsConfig) => {
    const sessionStartTime = useRef<number>(Date.now());
    const scrollDepth = useRef<number>(0);
    const maxScrollDepth = useRef<number>(0);
    const pageViewTracked = useRef<boolean>(false);
    const timeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const scrollMilestonesTracked = useRef<Set<number>>(new Set());

    const startSession = useCallback(async () => {
        if (!config.hasConsent || !isTokenAvailable() || !shouldTrackAnalytics()) return;

        // Check if session has already been started for this session ID
        const sessionStartedKey = `analytics_session_started_${config.sessionId}`;
        if (sessionStorage.getItem(sessionStartedKey) === 'true') {
            return; // Session already started, don't call API again
        }

        try {
            await request(
                APIS_METHOD.POST,
                APIS_ENDPOINTS.ANALYTICS_START_SESSION,
                {
                    session_id: config.sessionId,
                    entry_page: config.pageUrl,
                    device_type: getDeviceType(),
                    browser: getBrowser(),
                    os: getOS(),
                },
                false // Don't show loader for analytics
            );
            // Mark session as started
            sessionStorage.setItem(sessionStartedKey, 'true');
        } catch (error) {
            console.error('Failed to start session:', error);
            // Don't mark as started if API call failed
        }
    }, [config.sessionId, config.pageUrl, config.hasConsent]);

    const trackPageView = useCallback(async (timeSpent: number = 0, isExit: boolean = false) => {
        if (!config.hasConsent || !isTokenAvailable() || !shouldTrackAnalytics()) return;

        try {
            await request(
                APIS_METHOD.POST,
                APIS_ENDPOINTS.ANALYTICS_TRACK_PAGE_VIEW,
                {
                    session_id: config.sessionId,
                    page_url: config.pageUrl,
                    page_title: config.pageTitle,
                    time_spent_seconds: timeSpent,
                    scroll_depth_percent: scrollDepth.current,
                    max_scroll_depth_percent: maxScrollDepth.current,
                    is_bounce: timeSpent < 5 && scrollDepth.current < 25,
                    exit_page: isExit,
                },
                false
            );
        } catch (error) {
            console.error('Failed to track page view:', error);
        }
    }, [config.sessionId, config.pageUrl, config.pageTitle, config.hasConsent]);

    const trackScrollDepth = useCallback(async (depth: number) => {
        if (!config.hasConsent || !isTokenAvailable() || !shouldTrackAnalytics()) return;

        try {
            await request(
                APIS_METHOD.POST,
                APIS_ENDPOINTS.ANALYTICS_TRACK_SCROLL_DEPTH,
                {
                    session_id: config.sessionId,
                    page_url: config.pageUrl,
                    page_title: config.pageTitle,
                    scroll_depth_percent: depth,
                },
                false
            );
        } catch (error) {
            console.error('Failed to track scroll depth:', error);
        }
    }, [config.sessionId, config.pageUrl, config.pageTitle, config.hasConsent]);

    const trackEvent = useCallback(async ({
        eventType,
        eventName,
        elementId,
        elementClass,
        metadata,
    }: TrackEventParams) => {
        if (!config.hasConsent || !isTokenAvailable() || !shouldTrackAnalytics()) return;

        try {
            await request(
                APIS_METHOD.POST,
                APIS_ENDPOINTS.ANALYTICS_TRACK_EVENT,
                {
                    session_id: config.sessionId,
                    event_type: eventType,
                    event_name: eventName,
                    page_url: config.pageUrl,
                    page_title: config.pageTitle,
                    element_id: elementId,
                    element_class: elementClass,
                    metadata: metadata || {},
                },
                false
            );
        } catch (error) {
            console.error('Failed to track event:', error);
        }
    }, [config.sessionId, config.pageUrl, config.pageTitle, config.hasConsent]);

    const checkStuckUser = useCallback(async () => {
        if (!config.hasConsent || !isTokenAvailable() || !shouldTrackAnalytics()) return null;

        try {
            const timeOnPage = Math.floor((Date.now() - sessionStartTime.current) / 1000);

            const response = await requestWithParam(
                APIS_METHOD.GET,
                APIS_ENDPOINTS.ANALYTICS_CHECK_STUCK,
                {},
                {
                    session_id: config.sessionId,
                    page_url: config.pageUrl,
                    time_on_page_seconds: timeOnPage,
                },
                false
            );

            if (response?.data?.should_show_help) {
                return {
                    shouldShowHelp: true,
                    triggerReason: response.data.trigger_reason,
                    timeOnPageSeconds: response.data.time_on_page_seconds,
                };
            }

            return null;
        } catch (error) {
            console.error('Failed to check stuck user:', error);
            return null;
        }
    }, [config.sessionId, config.pageUrl, config.hasConsent]);

    const endSession = useCallback(async (reason: string = 'unknown') => {
        if (!config.hasConsent || !isTokenAvailable() || !shouldTrackAnalytics()) return;

        try {
            const timeSpent = Math.floor((Date.now() - sessionStartTime.current) / 1000);
            await request(
                APIS_METHOD.POST,
                APIS_ENDPOINTS.ANALYTICS_END_SESSION,
                {
                    session_id: config.sessionId,
                    page_url: config.pageUrl,
                    page_title: config.pageTitle,
                    time_spent_seconds: timeSpent,
                    max_scroll_depth_percent: maxScrollDepth.current,
                    reason,
                },
                false
            );
        } catch (error) {
            console.error('Failed to end session:', error);
        }
    }, [config.sessionId, config.pageUrl, config.pageTitle, config.hasConsent]);

    useEffect(() => {
        if (!config.hasConsent || !isTokenAvailable() || !shouldTrackAnalytics()) {
            return;
        }

        // Reset tracking state for new page
        sessionStartTime.current = Date.now();
        scrollDepth.current = 0;
        maxScrollDepth.current = 0;
        pageViewTracked.current = false;
        scrollMilestonesTracked.current = new Set();

        // Capture ref values at the start of the effect to avoid React hooks warning
        const effectStartTime = sessionStartTime.current;

        // Start session (only once per session - startSession checks if already started)
        startSession();

        // Track initial page view (only once per page)
        trackPageView(0, false);
        pageViewTracked.current = true;

        // Track scroll depth
        const handleScroll = () => {
            const doc = document.documentElement;
            const totalHeight = doc.scrollHeight;

            if (!totalHeight) {
                return;
            }

            const viewportBottom = window.scrollY + window.innerHeight;
            const rawPercent = (viewportBottom / totalHeight) * 100;
            const scrollPercent = Math.min(100, Math.round(rawPercent));

            scrollDepth.current = scrollPercent;
            if (scrollPercent > maxScrollDepth.current) {
                maxScrollDepth.current = scrollPercent;
            }

            // Track milestones (25%, 50%, 75%, 100%) - strictly once per milestone per page view
            const milestones = [25, 50, 75, 100];
            milestones.forEach((milestone) => {
                if (
                    scrollPercent >= milestone &&
                    !scrollMilestonesTracked.current.has(milestone)
                ) {
                    scrollMilestonesTracked.current.add(milestone);
                    trackScrollDepth(milestone);
                }
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        // Track time spent on page (update every 30 seconds, not 5 to reduce requests)
        timeIntervalRef.current = setInterval(() => {
            const timeSpent = Math.floor((Date.now() - sessionStartTime.current) / 1000);
            trackPageView(timeSpent, false);
        }, 30000); // Changed from 5000 to 30000 (30 seconds)

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (timeIntervalRef.current) {
                clearInterval(timeIntervalRef.current);
                timeIntervalRef.current = null;
            }

            // Track final time spent on exit using captured values
            const finalTimeSpent = Math.floor((Date.now() - effectStartTime) / 1000);
            if (pageViewTracked.current) {
                trackPageView(finalTimeSpent, true);
            }
        };
    }, [config.pageUrl, config.hasConsent, startSession, trackPageView, trackScrollDepth]);

    return {
        trackEvent,
        checkStuckUser,
        trackPageView,
        trackScrollDepth,
        endSession,
    };
};
